(function (w, d) {
  const instances = [];
  const videos = d.querySelectorAll('.js-video');
  const firstScriptTag = d.querySelectorAll('script')[0];
  const onLoadInstances = [];
  let hasVimeoLoad = false;
  const vimeoUrl = 'https://player.vimeo.com';

  /**
   * Set iframe element size.
   * @param {DOMNode} element          Iframe element.
   * @param {Array}   dimensions       Width/Height array.
   * @param {DOMNode} containerElement Container element.
   */
  function setIframeSize(element, dimensions, containerElement) {
    const width = dimensions[0];
    const height = dimensions[1];

    element.width = width;
    element.height = height;

    const format = height > width ? 'portrait' : 'landscape';

    if (containerElement && !containerElement.classList.contains('video--mobile')) {
      containerElement.classList.add(`video--${format}`);
      containerElement.style.paddingBottom = `calc(${dimensions[1] / dimensions[0]} * 100%)`;
    }
  }

  /**
   * Send data to Vimeo iframe.
   * @param  {string}  action Type of action.
   * @param  {mixed}   value  Data to send.
   * @param  {DOMNode} target Iframe.
   * @note Vimeo player event don't work as expected when multiple player, so this method is a workaround.
   * @see Inspired by https://codepen.io/filipbech/pen/Aybku.
   * @returns {void}
   */
  function vimeoPost(action, value, target) {
    const data = { method: action };

    if (value) {
      data.value = value;
    }

    target.contentWindow.postMessage(JSON.stringify(data), vimeoUrl);
  }

  /**
   * When Vimeo player is Ready to play.
   * @returns {void}
   */
  function initVimeoPlayer(element, playElement, containerElement, forcePlay) {
    if (element.classList.contains('is-init')) {
      return;
    }

    if (!Vimeo) {
      return;
    }

    element.classList.add('is-init');

    const player = new Vimeo.Player(element);

    player.on('ended', function () {
      player.element.classList.remove('is-play');
    });

    player.on('play', function () {
      player.element.classList.add('is-play');
    });

    // Get player dimensions to manage video ratio and responsive.
    player.on('loaded', function () {
      Promise.all([player.getVideoWidth(), player.getVideoHeight()])
        .then(function (dimensions) {
          setIframeSize(player.element, dimensions, containerElement);
        })
        .finally(function () {
          player.element.classList.add('is-ready');

          if (forcePlay === true) {
            vimeoPost('play', null, player.element);
          }
        });

      if (playElement) {
        playElement.addEventListener('click', function () {
          if (player.element.classList.contains('is-play')) {
            return;
          }

          vimeoPost('play', null, player.element);
        });
      }
    });
  }

  /**
   * Load player.
   * @param  {DOMNode} element          Iframe element.
   * @param  {string}  type             Player type (vimeo).
   * @param  {DOMNode} playButton       Play button.
   * @param  {DOMNode} containerElement Container element.
   */
  function initPlayer(element, type, playButton, containerElement, forcePlay) {
    // Lazy load iframe.
    if (element.dataset.src) {
      element.setAttribute('src', element.dataset.src);
    }

    if (type === 'vimeo') {
      initVimeoPlayer(element, playButton, containerElement, forcePlay);
    }
  }

  /**
   * Load Player API.
   * @param {string} type API type (vimeo).
   * @returns {DOMElement|bool}
   */
  function loadAPI(type, callback) {
    if (!firstScriptTag) {
      return false;
    }

    let APISrc = null;

    if (type === 'vimeo' && !hasVimeoLoad) {
      hasVimeoLoad = true;
      APISrc = 'https://player.vimeo.com/api/player.js';
    }

    if (APISrc !== null) {
      const tag = d.createElement('script');
      tag.setAttribute('defer', '');
      tag.src = APISrc;

      if (typeof callback === 'function') {
        tag.addEventListener('load', callback);
      }

      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      return tag;
    }

    return false;
  }

  /**
   * Init iframe.
   * @returns {void}
   */
  function initVideo(instance, forcePlay) {
    if (instances[instance.index].isInit || !instance.iframe || !instance.type) {
      return;
    }

    instances[instance.index].isInit = true;

    if (!forcePlay) {
      forcePlay = false;
    }

    const initMethod = initPlayer.bind(
      null,
      instance.iframe,
      instance.type,
      instance.playElement,
      instance.element,
      forcePlay,
    );

    let apiLoaded = false;

    if (instance.isFacade) {
      apiLoaded = loadAPI(instance.type, initMethod);
    }

    if (!apiLoaded) {
      initMethod();
    }
  }

  /**
   * On message function.
   * @param  {object} event Event.
   * @returns {void}
   */
  function onMessage(event) {
    if (event.origin !== vimeoUrl) {
      return;
    }

    let sender = null;
    const message = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

    if (!message || (!message.event && !message.method) || !message.player_id) {
      return;
    }

    sender = document.querySelector(`[data-playerid="${message.player_id}"]`);

    if (!sender) {
      return;
    }

    const target = sender.querySelector('iframe');

    if (message.event === 'ready' && target) {
      vimeoPost('addEventListener', 'play', target);
      vimeoPost('addEventListener', 'pause', target);
    }

    if (message.event === 'play') {
      target.classList.add('is-play');
    }

    if (message.event === 'pause') {
      target.classList.remove('is-play');
    }
  }

  /**
   * Load instancies.
   */
  function loadInstances() {
    for (const [i, videoElement] of videos.entries()) {
      const iframe = videoElement.querySelector('.js-video-iframe');
      const playElement = videoElement.querySelector('.js-video-play');
      const type = iframe && iframe.dataset.type ? iframe.dataset.type : null;

      instances[i] = {
        index: i,
        type,
        playElement,
        element: videoElement,
        iframe,
        isInit: false,
        isFacade: iframe.classList.contains('is-facade'),
      };

      if (instances[i].isFacade) {
        /*
          Load facade videos (the ones with background-image) later for performance reason.
        */
        const initVideoFn = initVideo.bind(null, instances[i]);

        instances[i].element.addEventListener('mouseenter', initVideoFn);
        instances[i].playElement.addEventListener('click', initVideoFn.bind(null, true));
        continue;
      }

      onLoadInstances.push(instances[i]);
    }
  }

  /**
   * Load API and videos who need to be loaded on load.
   * @returns {void}
   */
  function onLoad() {
    loadInstances();
    const vimeoInstances = onLoadInstances.filter(function (item) {
      return item.type === 'vimeo';
    });

    if (vimeoInstances.length > 0) {
      loadAPI('vimeo', function () {
        for (const vimeoInstance of vimeoInstances) {
          initVideo(vimeoInstance);
        }
      });
    }
  }

  /**
   * When message are send to window.
   */
  window.addEventListener('message', onMessage);

  /**
   * When DOM content is loaded.
   */
  window.addEventListener('DOMContentLoaded', onLoad);
})(window, document);
